import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Typography,
    Paper,
    ThemeProvider,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    useMediaQuery,
} from '@mui/material';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import { darkTheme } from '../../../theme/darkTheme';
import NavBar from '../../layout/NavBar';
import { TrainingApi, TestApi } from '../../../services/api';
import NavBarBot from '../../layout/NavBarBot';
import Notification from '../../util/notifications/Notification';
import HeaderCard from '../../layout/HeaderCard';
import LoadingPage from '../../layout/LoadingPage';
import { formatDate } from './components/trainingdetail/trainingDetailUtils';
import SortableExerciseCard from './components/trainingdetail/SortableExerciseCard';
import ExerciseAccordion from './components/trainingdetail/ExerciseAccordion';
import UebungAuswahlDialog from '../../shared/components/uebungen/UebungAuswahlDialog';

const TrainingDetail = () => {
    const isMobile = useMediaQuery(darkTheme.breakpoints.down('md'));
    const { id } = useParams();
    const navigate = useNavigate();
    const [, setShowNotification] = useState(false);
    const [loading, setLoading] = useState(true);
    const [session, setSession] = useState(null);
    const [ergebnisse, setErgebnisse] = useState([]);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [, setSaving] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editedSession, setEditedSession] = useState(null);
    const [editedErgebnisse, setEditedErgebnisse] = useState([]);
    const [activeId, setActiveId] = useState(null);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [alleUebungen, setAlleUebungen] = useState([]);
    const [filterKategorie, setFilterKategorie] = useState('');
    const [filterZielmuskel, setFilterZielmuskel] = useState('');
    const [selectedUebung, setSelectedUebung] = useState(null);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
    );

    useEffect(() => {
        fetchTrainingDetails();
        fetchAllUebungen(); // NEU
    }, [id]);

    const fetchTrainingDetails = async () => {
        try {
            setLoading(true);
            const sessionResponse = await TrainingApi.getSessionDetails(id);
            setSession(sessionResponse.data);

            const ergebnisseResponse = await TrainingApi.getSessionErgebnisse(id);

            // WICHTIG: Sortiere nach reihenfolge, falls vorhanden
            const sortedErgebnisse = ergebnisseResponse.data.sort((a, b) => {
                // Falls reihenfolge vorhanden ist, danach sortieren
                if (a.reihenfolge !== undefined && b.reihenfolge !== undefined) {
                    return a.reihenfolge - b.reihenfolge;
                }
                // Fallback: nach uebung_name oder uebung_id
                return (a.uebung_name || '').localeCompare(b.uebung_name || '');
            });

            setErgebnisse(sortedErgebnisse);
            setMessage({ type: 'success', text: 'Trainingsdetails geladen' });
        } catch (err) {
            console.error('Fehler beim Laden der Trainingsdetails:', err);
            setMessage({ type: 'error', text: 'Fehler beim Laden der Trainingsdetails' });
        } finally {
            setLoading(false);
        }
    };

    const fetchAllUebungen = async () => {
        try {
            const response = await TestApi.getAllUebungen();
            setAlleUebungen(response.data);
        } catch (err) {
            console.error('Fehler beim Laden der Übungen:', err);
        }
    };

    // Gruppiere Ergebnisse nach Übung
    const groupByExercise = () => {
        const grouped = {};
        ergebnisse.forEach((ergebnis) => {
            const key = ergebnis.uebung_name || 'Unbekannte Übung';
            if (!grouped[key]) {
                grouped[key] = {
                    uebung_name: key,
                    reihenfolge: ergebnis.reihenfolge || 999,
                    saetze: [],
                };
            }
            grouped[key].saetze.push(ergebnis);
        });

        // Sortiere nach Reihenfolge
        const sortedGrouped = Object.fromEntries(
            Object.entries(grouped).sort(([, a], [, b]) =>
                (a.reihenfolge || 999) - (b.reihenfolge || 999)
            )
        );

        return sortedGrouped;
    };

    const handleStartEdit = () => {
        setEditedSession({
            ...session,
            datum: session.datum ? new Date(session.datum).toISOString().split('T')[0] : ''
        });

        const grouped = {};
        ergebnisse.forEach(ergebnis => {
            if (!grouped[ergebnis.uebung_id]) {
                grouped[ergebnis.uebung_id] = {
                    uebung_id: ergebnis.uebung_id,
                    uebung_name: ergebnis.uebung_name || 'Unbekannte Übung',
                    kategorie: ergebnis.kategorie || '',
                    zielmuskel: ergebnis.zielmuskel || '',
                    reihenfolge: ergebnis.reihenfolge || 999, // Speichere die Reihenfolge
                    saetze: []
                };
            }
            grouped[ergebnis.uebung_id].saetze.push({
                satz_nummer: ergebnis.satz_nummer,
                wiederholungen: ergebnis.wiederholungen,
                gewicht_kg: ergebnis.gewicht_kg,
                notizen: ergebnis.notizen
            });
        });

        // Auffüllen auf genau 3 Sätze pro Übung
        Object.values(grouped).forEach(exercise => {
            while (exercise.saetze.length < 3) {
                exercise.saetze.push({
                    wiederholungen: null,
                    gewicht_kg: null,
                    notizen: null
                });
            }
        });

        // WICHTIG: Nach reihenfolge sortieren vor dem Setzen
        const sortedGrouped = Object.values(grouped).sort((a, b) =>
            (a.reihenfolge || 999) - (b.reihenfolge || 999)
        );

        setEditedErgebnisse(sortedGrouped);
        setIsEditing(true);
    };

    const handleCancelEdit = () => {
        setIsEditing(false);
        setEditedSession(null);
        setEditedErgebnisse([]);
    };

    const handleSaveEdit = async () => {
        setSaving(true);
        try {
            const alleErgebnisse = [];
            editedErgebnisse.forEach((exercise) => {
                exercise.saetze.forEach((satz, satzIdx) => {
                    if (satz.wiederholungen && satz.wiederholungen !== '' && satz.wiederholungen !== null) {
                        alleErgebnisse.push({
                            uebung_id: exercise.uebung_id,
                            satz_nummer: satzIdx + 1,
                            wiederholungen: parseInt(satz.wiederholungen),
                            gewicht_kg: satz.gewicht_kg && satz.gewicht_kg !== '' ? parseFloat(satz.gewicht_kg) : null,
                            notizen: satz.notizen || null
                        });
                    }
                });
            });

            const uebungenReihenfolge = editedErgebnisse.map((ex, idx) => ({
                uebung_id: ex.uebung_id,
                reihenfolge: idx + 1
            }));

            const formatDateForDB = (dateString) => {
                if (!dateString) return null;
                const date = new Date(dateString);
                return date.toISOString().split('T')[0];
            };

            const formatTimeForDB = (timeString) => {
                if (!timeString) return null;
                const date = new Date(timeString);
                return date.toTimeString().split(' ')[0];
            };

            const payload = {
                nutzer_id: session.nutzer_id,
                trainingsplan_id: session.trainingsplan_typ === 'custom'
                    ? session.custom_plan_id
                    : session.standard_plan_id,
                trainingsplan_typ: session.trainingsplan_typ || 'standard', // NEU: Plantyp aus Session
                datum: formatDateForDB(editedSession.datum),
                startzeit: formatTimeForDB(editedSession.startzeit),
                endzeit: formatTimeForDB(editedSession.endzeit),
                notizen: editedSession.notizen,
                ergebnisse: alleErgebnisse,
                uebungen_reihenfolge: uebungenReihenfolge
            };

            await TrainingApi.updateSession(id, payload);

            setMessage({ type: 'success', text: 'Training erfolgreich gespeichert' });
            setIsEditing(false);

            await fetchTrainingDetails();

            setTimeout(() => setMessage({ type: "", text: "" }), 3000);
        } catch (err) {
            console.error('Fehler beim Speichern:', err);
            console.error('Error response:', err.response?.data);
            setMessage({ type: 'error', text: err });
        } finally {
            setSaving(false);
        }
    };


    const handleSetChange = (uebungId, satzIdx, field, value) => {
        setEditedErgebnisse(prev => prev.map(ex => {
            if (ex.uebung_id === uebungId) {
                if (field === 'add') {
                    return {
                        ...ex,
                        saetze: [...ex.saetze, { wiederholungen: null, gewicht_kg: null, notizen: null }]
                    };
                }
                return {
                    ...ex,
                    saetze: ex.saetze.map((s, idx) => idx === satzIdx ? { ...s, [field]: value === '' ? null : value } : s
                    )
                };
            }
            return ex;
        }));
    };

    const handleDeleteSet = (uebungId, satzIdx) => {
        setEditedErgebnisse(prev => prev.map(ex => {
            if (ex.uebung_id === uebungId) {
                return {
                    ...ex,
                    saetze: ex.saetze.filter((_, idx) => idx !== satzIdx)
                };
            }
            return ex;
        }));
    };

    const handleDeleteExercise = (uebungId) => {
        if (window.confirm('Übung wirklich entfernen?')) {
            setEditedErgebnisse(prev => prev.filter(ex => ex.uebung_id !== uebungId));
        }
    };

    const handleAddUebung = () => {
        setSelectedUebung(null);
        setFilterKategorie('');
        setFilterZielmuskel('');
        setDialogOpen(true);
    };

    const handleDialogConfirm = () => {
        if (selectedUebung) {
            setEditedErgebnisse(prev => [...prev, {
                uebung_id: selectedUebung.id,
                uebung_name: selectedUebung.name,
                kategorie: selectedUebung.kategorie,
                zielmuskel: selectedUebung.zielmuskel,
                saetze: [
                    { wiederholungen: '', gewicht_kg: '', notizen: null },
                    { wiederholungen: '', gewicht_kg: '', notizen: null },
                    { wiederholungen: '', gewicht_kg: '', notizen: null },
                ]
            }]);
        }
        setDialogOpen(false);
    };

    const handleDragEnd = (event) => {
        const { active, over } = event;
        setActiveId(null);
        if (over && active.id !== over.id) {
            setEditedErgebnisse(prev => {
                const oldIndex = prev.findIndex(ex => ex.uebung_id === active.id);
                const newIndex = prev.findIndex(ex => ex.uebung_id === over.id);
                return arrayMove(prev, oldIndex, newIndex);
            });
        }
    };

    const handleDeleteSession = async () => {
        setDeleting(true);
        try {
            // Sende optional die Nutzer-ID mit, falls der Server diese zur Autorisierung erwartet
            const payload = session && session.nutzer_id ? { nutzer_id: session.nutzer_id } : undefined;
            await TrainingApi.deleteSession(id, payload);
            setDeleteDialogOpen(false);
            // Nach dem Löschen zurück zur Dashboard-Ansicht (früher '/login')
            navigate('/dashboard');
        } catch (err) {
            // Bessere Fehlerdiagnose: Status und Server-Antwort anzeigen
            console.error('Fehler beim Löschen:', err);
            if (err.response) {
                console.error('Antwort-Status:', err.response.status);
                console.error('Antwort-Daten:', err.response.data);
                if (err.response.status === 403) {
                    setMessage({ type: 'error', text: 'Keine Berechtigung, dieses Training zu löschen (403)' });
                } else if (err.response.data?.error) {
                    setMessage({ type: 'error', text: err.response.data.error });
                } else {
                    setMessage({ type: 'error', text: 'Training konnte nicht gelöscht werden' });
                }
            } else {
                setMessage({ type: 'error', text: 'Training konnte nicht gelöscht werden' });
            }
            setDeleteDialogOpen(false);
        } finally {
            setDeleting(false);
        }
    };

    const groupedExercises = groupByExercise();

    // Optionen & Filterwerte für den geteilten Übungs-Auswahldialog
    const kategorien = [...new Set(alleUebungen.map(u => u.kategorie))];
    const zielmuskeln = [...new Set(alleUebungen.map(u => u.zielmuskel))];
    const uebungOptions = alleUebungen.filter(u =>
        (!filterKategorie || u.kategorie === filterKategorie) &&
        (!filterZielmuskel || u.zielmuskel === filterZielmuskel)
    );

    if (loading) {
        return (
            <LoadingPage />
        );
    }

    if (!session) {
        return (
            <>
                <ThemeProvider theme={darkTheme}>
                    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
                        <NavBar />
                        <Container maxWidth="sm" sx={{ pt: 4, px: 2 }}>
                            <Typography color="error">{message.text || 'Training nicht gefunden'}</Typography>
                        </Container>
                    </Box>
                </ThemeProvider>
                <NavBarBot mainBtnF={null} />
            </>
        );
    }

    return (
        <>
            <ThemeProvider theme={darkTheme}>
                <Box sx={{
                    bgcolor: 'background.default', minHeight: '100vh', pb: 10
                }}>
                    <NavBar />
                    <Container maxWidth="sm" sx={{ pt: 2, px: { xs: 1, sm: 2 } }}>
                        {/* Header */}

                        <HeaderCard title={isEditing ? "Training bearbeiten" : session.trainingsplan_name || 'Trainingseinheit'} />

                        {message.status === "error" || message.status === "success" && (
                            <Notification
                                type={message.type}
                                message={message.text}
                                onClose={() => {
                                    setShowNotification(true);
                                    setMessage({ type: "", text: "" });
                                }}
                            />
                        )}
                        {!isEditing && !isMobile && (
                            <Button variant="contained" onClick={() => handleStartEdit()}>Bearbeiten</Button>
                        )}
                        {isEditing && !isMobile && (
                            <Button variant="contained" onClick={() => handleSaveEdit()}>Speichern</Button>
                        )}
                        {isEditing && !isMobile && (
                            <Button variant="contained" onClick={() => handleCancelEdit()}>Abbrechen</Button>
                        )}
                        {isEditing && !isMobile && (
                            <Button variant="contained" onClick={() => setDeleteDialogOpen(true)}>Löschen</Button>
                        )}
                        {/* Session Info Card */}
                        <Paper
                            elevation={2}
                            sx={{
                                mb: 3,
                                p: 2,
                                background: 'linear-gradient(145deg, #1e293b 0%, #0f172a 100%)',
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                borderRadius: '16px'
                            }}
                        >
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {!isEditing && (
                                    <Box sx={{ display: "flex" }}>
                                        {formatDate(session.datum)}
                                    </Box>
                                )}
                                {isEditing && (
                                    <>
                                        <TextField
                                            label="Datum"
                                            type="date"
                                            value={editedSession?.datum || ''}
                                            onChange={(e) => setEditedSession({ ...editedSession, datum: e.target.value })}
                                            size="small"
                                            fullWidth
                                            InputLabelProps={{ shrink: true }}
                                            sx={{ mt: 1 }}
                                        />
                                        <TextField
                                            label="Notizen"
                                            value={editedSession?.notizen || ''}
                                            onChange={(e) => setEditedSession({ ...editedSession, notizen: e.target.value })}
                                            fullWidth
                                            multiline
                                            rows={2}
                                            size="small"
                                            sx={{ mt: 2 }}
                                        />
                                    </>
                                )}
                            </Box>

                            {session.notizen && !isEditing && (
                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(59, 130, 246, 0.2)' }}>
                                    <Typography variant="caption" color="#93c5fd" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                        Notizen
                                    </Typography>
                                    <Typography variant="body2" color="#cbd5e1">
                                        {session.notizen}
                                    </Typography>
                                </Box>
                            )}
                        </Paper>

                        {/* Übungen */}
                        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <Typography variant="h6" fontWeight={700} color="#e0f2fe">
                                Übungen ({(isEditing ? editedErgebnisse : Object.keys(groupedExercises)).length})
                            </Typography>
                            {isEditing && (
                                <Button
                                    startIcon={<AddIcon />}
                                    onClick={handleAddUebung}
                                    variant="contained"
                                    size="small"
                                    sx={{ bgcolor: '#8b5cf6', '&:hover': { bgcolor: '#7c3aed' } }}
                                >
                                    Übung hinzufügen
                                </Button>
                            )}
                        </Box>

                        {(isEditing ? editedErgebnisse.length === 0 : Object.keys(groupedExercises).length === 0) ? (
                            <Paper sx={{ p: 3, bgcolor: '#1e293b', textAlign: 'center', borderRadius: '16px' }}>
                                <Typography color="#93c5fd">
                                    Keine Übungen für dieses Training gefunden
                                </Typography>
                            </Paper>
                        ) : isEditing ? (
                            <DndContext
                                sensors={sensors}
                                collisionDetection={closestCenter}
                                onDragStart={({ active }) => setActiveId(active.id)}
                                onDragEnd={handleDragEnd}
                            >
                                <SortableContext
                                    items={editedErgebnisse.map(ex => ex.uebung_id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                        {editedErgebnisse.map((exercise) => (
                                            <SortableExerciseCard
                                                key={exercise.uebung_id}
                                                exercise={exercise}
                                                onEdit={handleSetChange}
                                                onDelete={() => handleDeleteExercise(exercise.uebung_id)}
                                                onDeleteSet={handleDeleteSet}
                                                isEditing={true}
                                                isDragging={activeId === exercise.uebung_id}
                                            />
                                        ))}
                                    </Box>
                                </SortableContext>
                            </DndContext>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {Object.entries(groupedExercises).map(([exerciseName, exercise], index) => (
                                    <ExerciseAccordion
                                        key={index}
                                        exerciseName={exerciseName}
                                        exercise={exercise}
                                        defaultExpanded={index === 0}
                                    />
                                ))}
                            </Box>
                        )}
                    </Container>

                    {/* Dialog zum Hinzufügen von Übungen */}
                    <UebungAuswahlDialog
                        open={dialogOpen}
                        onClose={() => setDialogOpen(false)}
                        onConfirm={handleDialogConfirm}
                        isEdit={false}
                        filterKategorie={filterKategorie}
                        onFilterKategorie={setFilterKategorie}
                        filterZielmuskel={filterZielmuskel}
                        onFilterZielmuskel={setFilterZielmuskel}
                        kategorien={kategorien}
                        zielmuskeln={zielmuskeln}
                        options={uebungOptions}
                        selectedUebung={selectedUebung}
                        onSelectUebung={setSelectedUebung}
                    />
                </Box>
                {/* Lösch-Dialog */}
                <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                    <DialogTitle sx={{ bgcolor: '#1e293b', color: '#e0f2fe' }}>
                        Training löschen?
                    </DialogTitle>
                    <DialogContent sx={{ bgcolor: '#0f172a', pt: 2 }}>
                        <Typography color="#cbd5e1">
                            Möchtest du diese Trainingseinheit wirklich unwiderruflich löschen?
                        </Typography>
                    </DialogContent>
                    <DialogActions sx={{ bgcolor: '#0f172a', p: 2, display: "flex", justifyContent: "space-evenly" }}>
                        <Button
                            onClick={() => handleDeleteSession()}
                            sx={{ color: '#94a3b8' }}
                            disabled={deleting}
                        >
                            Fortfahren
                        </Button>
                        <Button
                            onClick={() => setDeleteDialogOpen(false)}
                            sx={{ color: '#94a3b8' }}
                            disabled={deleting}
                        >
                            Abbrechen
                        </Button>
                    </DialogActions>
                </Dialog>
            </ThemeProvider >
            <NavBarBot
                mainBtnF={handleStartEdit}
                mainBtnTxt={<EditIcon />}

                // sideBtn1F={!isEditing ? (() => setDeleteDialogOpen(true)) : null}
                // sideBtn1Icon={!isEditing ? <DeleteIcon /> : null}

                sideBtn2F={isEditing ? (handleCancelEdit) : (() => setDeleteDialogOpen(true))}
                sideBtn2Icon={isEditing ? (<CancelIcon />) : <DeleteIcon />}

                sideBtn3F={isEditing ? (handleSaveEdit) : (null)}
                sideBtn3Icon={isEditing ? (<SaveIcon />) : (null)} />

        </>
    );
};

export default TrainingDetail;
